import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, User, Bot, Loader2, Mail, ArrowRight, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple markdown renderer for chat messages
const renderMarkdown = (text) => {
  if (!text) return null;
  
  // Split into paragraphs
  const paragraphs = text.split(/\n\n+/);
  
  return paragraphs.map((paragraph, pIndex) => {
    // Check if it's a bullet list
    if (paragraph.match(/^[-•*]\s/m)) {
      const items = paragraph.split(/\n/).filter(line => line.trim());
      return (
        <ul key={pIndex} className="list-disc list-inside space-y-1 my-2">
          {items.map((item, iIndex) => {
            const content = item.replace(/^[-•*]\s*/, '');
            return <li key={iIndex} className="text-sm">{renderInlineMarkdown(content)}</li>;
          })}
        </ul>
      );
    }
    
    // Check if it's a numbered list
    if (paragraph.match(/^\d+\.\s/m)) {
      const items = paragraph.split(/\n/).filter(line => line.trim());
      return (
        <ol key={pIndex} className="list-decimal list-inside space-y-1 my-2">
          {items.map((item, iIndex) => {
            const content = item.replace(/^\d+\.\s*/, '');
            return <li key={iIndex} className="text-sm">{renderInlineMarkdown(content)}</li>;
          })}
        </ol>
      );
    }
    
    // Regular paragraph - handle line breaks within
    const lines = paragraph.split(/\n/);
    return (
      <p key={pIndex} className="my-2">
        {lines.map((line, lIndex) => (
          <React.Fragment key={lIndex}>
            {renderInlineMarkdown(line)}
            {lIndex < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
};

// Render inline markdown (bold, links)
const renderInlineMarkdown = (text) => {
  if (!text) return null;
  
  // Convert **bold** to <strong>
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    // Convert /path links to clickable
    const linkParts = part.split(/(\/?[a-z-]+(?:-[a-z]+)*)/gi);
    return linkParts.map((linkPart, lIndex) => {
      if (linkPart.match(/^\/[a-z-]+/i)) {
        return (
          <a 
            key={`${index}-${lIndex}`}
            href={linkPart}
            className="text-equipr-yellow hover:underline"
          >
            {linkPart}
          </a>
        );
      }
      return linkPart;
    });
  });
};

const CHAT_STORAGE_KEY = 'equipr_chat_state';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffEmail, setHandoffEmail] = useState('');
  const [handoffMessage, setHandoffMessage] = useState('');
  const [handoffSubmitted, setHandoffSubmitted] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Lead capture state
  const [showLeadCapture, setShowLeadCapture] = useState(true);
  const [visitorName, setVisitorName] = useState('');
  const [visitorCompany, setVisitorCompany] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Load chat state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(CHAT_STORAGE_KEY);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.messages?.length > 0) {
          setMessages(parsed.messages);
          setSessionId(parsed.sessionId);
          setVisitorName(parsed.visitorName || '');
          setVisitorCompany(parsed.visitorCompany || '');
          setVisitorEmail(parsed.visitorEmail || '');
          setVisitorPhone(parsed.visitorPhone || '');
          setShowLeadCapture(false); // Don't show lead capture if we have existing chat
          setHandoffEmail(parsed.visitorEmail || '');
        }
      } catch (e) {
        console.error('Error loading chat state:', e);
      }
    }
    setIsInitialized(true);
  }, []);

  // Save chat state to localStorage whenever it changes
  useEffect(() => {
    if (!isInitialized) return;
    
    const stateToSave = {
      messages,
      sessionId,
      visitorName,
      visitorCompany,
      visitorEmail,
      visitorPhone,
    };
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(stateToSave));
  }, [messages, sessionId, visitorName, visitorCompany, visitorEmail, visitorPhone, isInitialized]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle lead capture form submission
  const handleLeadCapture = (e) => {
    e.preventDefault();
    setShowLeadCapture(false);
    
    // Add personalised welcome message
    const firstName = visitorName.split(' ')[0];
    setMessages([{
      role: 'assistant',
      content: `Hi ${firstName}! 👋 Great to meet you${visitorCompany ? ` from ${visitorCompany}` : ''}.\n\nI'm EQUIPR's assistant and I'm here to help you:\n- Learn about our plant and crane hire software\n- Answer questions about features and pricing\n- Help you book a personalised demo\n\nWhat brings you to EQUIPR today?`
    }]);
  };

  // Skip lead capture
  const handleSkipLeadCapture = () => {
    setShowLeadCapture(false);
    setMessages([{
      role: 'assistant',
      content: "Hi! 👋 I'm EQUIPR's assistant.\n\nI can help you:\n- Learn about our plant and crane hire software\n- Answer questions about features and pricing\n- Help you book a personalised demo\n\nWhat brings you to EQUIPR today?"
    }]);
  };

  // Clear chat (for testing or user request)
  const clearChat = () => {
    localStorage.removeItem(CHAT_STORAGE_KEY);
    setMessages([]);
    setSessionId(null);
    setVisitorName('');
    setVisitorCompany('');
    setVisitorEmail('');
    setVisitorPhone('');
    setShowLeadCapture(true);
    setShowHandoff(false);
    setHandoffSubmitted(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage,
        visitor_name: visitorName || null,
        visitor_email: visitorEmail || null,
        visitor_company: visitorCompany || null,
        visitor_phone: visitorPhone || null
      });

      setSessionId(response.data.session_id);
      
      // Add AI response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);

      // Check if human handoff is needed
      if (response.data.needs_human) {
        setShowHandoff(true);
        // Pre-fill email if we have it
        if (visitorEmail) setHandoffEmail(visitorEmail);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I'm having trouble connecting right now. Please try again or contact us directly at our Contact page."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHandoffSubmit = async (e) => {
    e.preventDefault();
    if (!handoffEmail.trim()) return;

    try {
      await axios.post(`${API}/chat/handoff`, null, {
        params: {
          session_id: sessionId,
          email: handoffEmail,
          message: handoffMessage
        }
      });
      setHandoffSubmitted(true);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Thanks! Our team will reach out to you shortly at " + handoffEmail + ". Is there anything else I can help with in the meantime?"
      }]);
      setShowHandoff(false);
    } catch (error) {
      console.error('Handoff error:', error);
    }
  };

  const quickActions = [
    { label: 'Book a Demo', action: () => window.location.href = '/book-a-demo' },
    { label: 'See Features', action: () => setInputValue('What are EQUIPR\'s main features?') },
    { label: 'Pricing Info', action: () => setInputValue('How does EQUIPR pricing work?') },
  ];

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        data-testid="chat-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-equipr-yellow hover:bg-equipr-yellow-hover text-equipr-dark rounded-full shadow-lg flex items-center justify-center transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
            style={{ height: '500px', maxHeight: 'calc(100vh - 140px)' }}
          >
            {/* Header */}
            <div className="bg-equipr-dark px-4 py-3 flex items-center gap-3">
              <div className="w-10 h-10 bg-equipr-yellow rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-equipr-dark" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-sm">EQUIPR Assistant</h3>
                <p className="text-white/60 text-xs">Ask me anything about EQUIPR</p>
              </div>
              {messages.length > 0 && (
                <button 
                  onClick={clearChat}
                  className="text-white/60 hover:text-white transition-colors p-1"
                  title="Start new chat"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Lead Capture Form */}
            {showLeadCapture ? (
              <div className="flex-1 flex flex-col p-6 bg-gray-50">
                <div className="flex-1 flex flex-col justify-center">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-equipr-yellow rounded-full flex items-center justify-center mx-auto mb-4">
                      <Bot className="w-8 h-8 text-equipr-dark" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Welcome to EQUIPR!</h3>
                    <p className="text-gray-600 text-sm">
                      I'm here to help you learn about our plant & crane hire software. 
                      Let me know who I'm chatting with!
                    </p>
                  </div>
                  
                  <form onSubmit={handleLeadCapture} id="equipr-chat-lead-capture" name="equipr-chat-lead-capture" className="space-y-3">
                    <div>
                      <Input
                        placeholder="Your name *"
                        value={visitorName}
                        onChange={(e) => setVisitorName(e.target.value)}
                        required
                        className="text-sm border-gray-200 focus:border-equipr-yellow"
                        data-testid="chat-visitor-name"
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Company name *"
                        value={visitorCompany}
                        onChange={(e) => setVisitorCompany(e.target.value)}
                        required
                        className="text-sm border-gray-200 focus:border-equipr-yellow"
                        data-testid="chat-visitor-company"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Email (optional)"
                        value={visitorEmail}
                        onChange={(e) => setVisitorEmail(e.target.value)}
                        className="text-sm border-gray-200 focus:border-equipr-yellow"
                        data-testid="chat-visitor-email"
                      />
                    </div>
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone (optional)"
                        value={visitorPhone}
                        onChange={(e) => setVisitorPhone(e.target.value)}
                        className="text-sm border-gray-200 focus:border-equipr-yellow"
                        data-testid="chat-visitor-phone"
                      />
                    </div>
                    <Button 
                      type="submit"
                      className="w-full bg-equipr-yellow text-equipr-dark hover:bg-equipr-yellow-hover font-semibold"
                      data-testid="chat-start-btn"
                    >
                      Start Chatting
                    </Button>
                  </form>
                  
                  <button
                    onClick={handleSkipLeadCapture}
                    className="text-gray-400 hover:text-gray-600 text-xs mt-4 text-center"
                  >
                    Skip for now
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 bg-equipr-yellow rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <Bot className="w-4 h-4 text-equipr-dark" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                          message.role === 'user'
                            ? 'bg-equipr-dark text-white rounded-br-md text-sm'
                            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md shadow-sm'
                        }`}
                      >
                        {message.role === 'assistant' ? (
                          <div className="text-sm [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">
                            {renderMarkdown(message.content)}
                          </div>
                        ) : (
                          message.content
                        )}
                      </div>
                      {message.role === 'user' && (
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <User className="w-4 h-4 text-gray-600" />
                        </div>
                      )}
                    </motion.div>
                  ))}
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-2 justify-start"
                >
                  <div className="w-8 h-8 bg-equipr-yellow rounded-full flex items-center justify-center">
                    <Bot className="w-4 h-4 text-equipr-dark" />
                  </div>
                  <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md border border-gray-200 shadow-sm">
                    <Loader2 className="w-5 h-5 text-equipr-yellow animate-spin" />
                  </div>
                </motion.div>
              )}

              {/* Human Handoff Form */}
              {showHandoff && !handoffSubmitted && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-equipr-yellow/10 border border-equipr-yellow/30 rounded-lg p-4"
                >
                  <p className="text-sm text-equipr-dark font-medium mb-3">
                    Would you like our team to reach out to you?
                  </p>
                  <form onSubmit={handleHandoffSubmit} id="equipr-chat-handoff" name="equipr-chat-handoff" className="space-y-2">
                    <Input
                      type="email"
                      placeholder="Your email"
                      value={handoffEmail}
                      onChange={(e) => setHandoffEmail(e.target.value)}
                      className="text-sm h-9"
                      required
                    />
                    <Textarea
                      placeholder="Any additional details? (optional)"
                      value={handoffMessage}
                      onChange={(e) => setHandoffMessage(e.target.value)}
                      className="text-sm resize-none"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button 
                        type="submit" 
                        size="sm" 
                        className="bg-equipr-yellow text-equipr-dark hover:bg-equipr-yellow-hover"
                      >
                        <Mail className="w-4 h-4 mr-1" /> Contact Me
                      </Button>
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setShowHandoff(false)}
                      >
                        Not now
                      </Button>
                    </div>
                  </form>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {messages.length <= 1 && (
              <div className="px-4 py-2 bg-white border-t border-gray-100">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={action.action}
                      className="flex-shrink-0 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded-full transition-colors flex items-center gap-1"
                    >
                      {action.label}
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <form onSubmit={handleSendMessage} id="equipr-chat-message" name="equipr-chat-message" className="p-3 bg-white border-t border-gray-200">
              <div className="flex gap-2">
                <Input
                  data-testid="chat-input"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 text-sm border-gray-200 focus:border-equipr-yellow"
                  disabled={isLoading}
                />
                <Button
                  data-testid="chat-send-btn"
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="bg-equipr-yellow text-equipr-dark hover:bg-equipr-yellow-hover disabled:opacity-50"
                  size="icon"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
